/**
 * Snapshot-based undo/redo for the (immutable) project document. Snapshots
 * share structure with the live document, so memory cost is proportional to
 * what changed. A "group" turns a continuous gesture (e.g. a slider drag)
 * into a single undo step.
 */
export class History<T> {
  private past: T[] = [];
  private future: T[] = [];
  private groupOpen = false;
  private groupRecorded = false;

  constructor(private limit = 100) {}

  get canUndo(): boolean { return this.past.length > 0; }
  get canRedo(): boolean { return this.future.length > 0; }

  /** Call with the state *before* an edit. Inside an open group only the first edit is recorded. */
  record(before: T): void {
    if (this.groupOpen && this.groupRecorded) return;
    this.past.push(before);
    if (this.past.length > this.limit) this.past.shift();
    this.future = [];
    if (this.groupOpen) this.groupRecorded = true;
  }

  beginGroup(): void {
    this.groupOpen = true;
    this.groupRecorded = false;
  }

  endGroup(): void {
    this.groupOpen = false;
    this.groupRecorded = false;
  }

  /** Returns the state to restore, or null. `current` becomes redoable. */
  undo(current: T): T | null {
    const prev = this.past.pop();
    if (prev === undefined) return null;
    this.future.push(current);
    this.endGroup();
    return prev;
  }

  redo(current: T): T | null {
    const next = this.future.pop();
    if (next === undefined) return null;
    this.past.push(current);
    this.endGroup();
    return next;
  }

  clear(): void {
    this.past = [];
    this.future = [];
    this.endGroup();
  }
}
